<template>
  <div>
    <template slot="header">
      <div class="row">
        <div class="col-6">
          <h3 class="mb-0">{{ $t("CAFETERIAS.CAFETERIAS_LIST") }}</h3>
        </div>
        <div class="col-6 text-right">
          <base-button
            type="primary"
            icon
            size="sm"
            @click="addCafeteria()"
            v-if="$currentUserCan($permissions.PERM_CREATE_CAFETERIAS)"
          >
            <span class="btn-inner--icon">
              <i class="fas fa-plus"></i>
            </span>
            <span class="btn-inner--text">
              {{ $t("CAFETERIAS.ADD_CAFETERIA") }}
            </span>
          </base-button>
        </div>
      </div>
    </template>
    <div>
      <div
        class="col-12 d-flex flex-wrap filter-row"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <organization-selector
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />

        <establishment-selector
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
          @establishmentChanged="
            (establishmentId) => (selectedEstablishment = establishmentId)
          "
        />

        <el-select
          class="select-primary"
          v-model="selectedOperationType"
          :placeholder="$t('CAFETERIAS.OPERATION_TYPE')"
          clearable
        >
          <el-option
            class="select-primary"
            v-for="(operationType, key) in OPERATION_TYPES"
            :key="key"
            :label="$t('CAFETERIAS.OPERATION_TYPE_' + operationType)"
            :value="operationType"
          >
          </el-option>
        </el-select>

        <el-select
          class="select-primary"
          v-model="selectedOrderType"
          :placeholder="$t('CAFETERIAS.ORDER_TYPE')"
          clearable
        >
          <el-option
            class="select-primary"
            v-for="(orderType, key) in ORDER_TYPES"
            :key="key"
            :label="$t('CAFETERIAS.ORDER_TYPE_' + orderType)"
            :value="orderType"
          >
          </el-option>
        </el-select>

        <el-select
          class="select-primary"
          v-model="selectedServiceOptions"
          :placeholder="$t('CAFETERIAS.SERVICE_OPTIONS')"
          clearable
        >
          <el-option
            class="select-primary"
            v-for="(serviceOption, key) in COUNTER_SERVICE_TYPES"
            :key="key"
            :label="$t('CAFETERIAS.SERVICE_OPTION_' + serviceOption)"
            :value="serviceOption"
          >
          </el-option>
        </el-select>

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.NAME')"
            clearable
          />
        </div>
      </div>

      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="cafeterias"
        @sort-change="sortChange"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>

        <el-table-column
          :label="$t('COMMON.NAME')"
          prop="name"
          sortable="custom"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <span>
              {{ `${row.name}` }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.ESTABLISHMENT')"
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
          min-width="250px"
        >
          <template v-slot="{ row }">
            <establishment :establishment="row.establishment" />
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('CAFETERIAS.ORDER_TYPE')"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <span v-if="!!row.order_type">
              {{ $t("CAFETERIAS.ORDER_TYPE_" + row.order_type) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('CAFETERIAS.OPERATION_TYPE')"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <span v-if="!!row.operation_type">
              {{ $t("CAFETERIAS.OPERATION_TYPE_" + row.operation_type) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.ORGANIZATION')"
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          min-width="250px"
        >
          <template v-slot="{ row }">
            <organization :organization="row.organization" />
          </template>
        </el-table-column>

        <el-table-column min-width="100px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_CAFETERIAS)"
            >
              <a
                type="text"
                @click="viewCafeteria(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_EDIT_CAFETERIAS)"
            >
              <a
                type="text"
                @click="editCafeteria(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.DELETE')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_DELETE_CAFETERIAS)"
            >
              <a
                type="text"
                @click="deleteCafeteria(row.id)"
                class="table-action table-action-delete"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}

          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Option,
  Select,
  Table,
  TableColumn,
  Tooltip,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import Establishment from "@/components/Establishment.vue";
import { 
  ORDER_TYPES, 
  ORDER_TYPE_ONLINE, 
  ORDER_TYPE_COUNTER,
  COUNTER_SERVICE_TYPES,
  OPERATION_TYPES
} from "@/constants/cafeteria";

export default {
  name: "cafeteria-list-table",

  components: {
    BasePagination,
    OrganizationSelector,
    EstablishmentSelector,
    Establishment,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    filterEstablishment: {
      type: String,
      default: null,
      description: "Establishment id",
    },
    filterOrderType: {
      type: String,
      default: null,
      description: "Order Type filter",
    },
    filterOperationType: {
      type: String,
      default: null,
      description: "Operation Type filter (PRODUCTION / SATELLITE / AUTONOMOUS)",
    },
  },

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      cafeterias: [],
      loading: true,
      selectedOrganization: null,
      selectedEstablishment: null,
      ORDER_TYPES,
      ORDER_TYPE_ONLINE,
      ORDER_TYPE_COUNTER,
      COUNTER_SERVICE_TYPES,
      OPERATION_TYPES,
      selectedOrderType: this.filterOrderType || null,
      selectedOperationType: this.filterOperationType || null,
      selectedServiceOptions: [],
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedTags: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedLocations: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrderType: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedServiceOptions: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOperationType: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterOrderType: {
      handler(val) {
        this.selectedOrderType = val;
      },
      immediate: true,
    },
    filterOperationType: {
      handler(val) {
        this.selectedOperationType = val;
      },
      immediate: true,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "organization,establishment",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }
        if (this.filterEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.filterEstablishment,
            },
          };
        }
        if (this.selectedEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.selectedEstablishment,
            },
          };
        }
        if (this.selectedTags) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              tags: this.selectedTags.map((item) => item.id),
            },
          };
        }
        if (this.selectedLocations) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              allowedLocations: this.selectedLocations.map((loc) => loc.id),
            },
          };
        }

        // Add order type filter
        if (this.selectedOrderType) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              order_type: this.selectedOrderType,
            },
          };
        }

        // Add operation type filter (production / satellite / autonomous)
        if (this.selectedOperationType) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              operation_type: this.selectedOperationType,
            },
          };
        }

        // Add service options filter
        if (this.selectedServiceOptions && this.selectedServiceOptions.length) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              service_options: this.selectedServiceOptions,
            },
          };
        }

        await this.$store.dispatch("cafeterias/list", params);
        this.cafeterias = this.$store.getters["cafeterias/list"];
        this.total = this.$store.getters["cafeterias/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async deleteCafeteria(id) {
      const confirmation = await swal.fire({
        title: this.$t("CAFETERIAS.DELETE_THIS_CAFETERIA"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("cafeterias/destroy", id);
          await this.getList();
          this.$notify({
            type: "success",
            message: this.$t("CAFETERIAS.CAFETERIA_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewCafeteria(cafeteria) {
      this.$router.push({
        name: "View Cafeteria",
        params: { id: cafeteria.id },
      });
    },

    async editCafeteria(row) {
      this.$router.push({
        name: "Edit Cafeteria",
        params: { id: row.id },
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
